import React, { useState } from 'react';
import {
  ThemeProvider,
  darkTheme,
  Box,
  Typography,
  Stack,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
} from 'omaha-design-system';
import { Helmet } from 'react-helmet';
import Header from '../common/ToolsHeader';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import MobileCover from '../common/MobileCover';
import Authentication from '../common/Authentication';
import { useGames, useRotation, NBA_TEAMS, useSubscriptionCheck } from './hooks';
import RotationChart from './RotationChart';
import { ROTATION_COLORS } from './constants';
import { UnlockOverlay } from './UnlockOverlay';
import { SubscriptionRequiredOverlay } from './SubscriptionRequiredOverlay';

// Empty state skeleton component
const RotationEmptyState: React.FC<{ message: string; subMessage?: string; isLoading?: boolean }> = ({ message, subMessage, isLoading }) => {
  // Generate fake player rows for skeleton
  const skeletonPlayers = [
    { name: 'Player 1', widths: [15, 25, 20, 30] },
    { name: 'Player 2', widths: [20, 15, 25, 20] },
    { name: 'Player 3', widths: [10, 30, 15, 25] },
    { name: 'Player 4', widths: [25, 20, 10, 20] },
    { name: 'Player 5', widths: [15, 25, 30, 15] },
  ];

  return (
    <Box sx={{ mt: 2 }}>
      {/* Skeleton chart container */}
      <Box
        sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.02)',
          borderRadius: '8px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          p: 3,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Quarter markers */}
        <Stack direction="row" justifyContent="space-between" sx={{ mb: 2, px: 12 }}>
          {['Q1', 'Q2', 'Q3', 'Q4'].map((q) => (
            <Typography
              key={q}
              sx={{
                color: 'rgba(255, 255, 255, 0.2)',
                fontSize: '12px',
                fontWeight: 600,
              }}
            >
              {q}
            </Typography>
          ))}
        </Stack>

        {/* Skeleton player rows */}
        <Stack spacing={1.5}>
          {skeletonPlayers.map((player, idx) => (
            <Stack key={idx} direction="row" alignItems="center" spacing={2}>
              {/* Player name placeholder */}
              <Box
                sx={{
                  width: 80,
                  height: 16,
                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  borderRadius: '4px',
                }}
              />
              {/* Rotation bar placeholders */}
              <Stack direction="row" spacing={0.5} sx={{ flex: 1 }}>
                {player.widths.map((width, i) => (
                  <Box
                    key={i}
                    sx={{
                      width: `${width}%`,
                      height: 24,
                      backgroundColor: 'rgba(74, 159, 157, 0.15)',
                      borderRadius: '4px',
                    }}
                  />
                ))}
              </Stack>
            </Stack>
          ))}
        </Stack>

        {/* Overlay message */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 10, 22, 0.85)',
          }}
        >
          {isLoading && (
            <CircularProgress
              size={32}
              sx={{ color: ROTATION_COLORS.accent.teal, mb: 2 }}
            />
          )}
          <Typography
            sx={{
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: '18px',
              fontWeight: 600,
              mb: 1,
            }}
          >
            {message}
          </Typography>
          {subMessage && (
            <Typography
              sx={{
                color: 'rgba(255, 255, 255, 0.5)',
                fontSize: '14px',
              }}
            >
              {subMessage}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      refetchOnWindowFocus: false,
    },
  },
});

const RotationsContent: React.FC = () => {
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [selectedGameId, setSelectedGameId] = useState<string | null>(null);

  const { isLoading: isSubLoading, isAuthenticated, hasActiveSubscription } = useSubscriptionCheck();
  const { data: games, isLoading: gamesLoading, error: gamesError } = useGames(selectedTeamId);
  const { data: rotation, isLoading: rotationLoading, error: rotationError } = useRotation(selectedGameId);

  // Determine what content to show based on auth/subscription state
  const renderContent = () => {
    // Show sign-in screen when not authenticated
    if (!isSubLoading && !isAuthenticated) {
      return <UnlockOverlay />;
    }

    // Show loading while checking subscription
    if (isSubLoading) {
      return (
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
        </Box>
      );
    }

    // Show subscription required screen if user has no active NBA subscription
    if (hasActiveSubscription === false) {
      return <SubscriptionRequiredOverlay />;
    }

    // Show main rotations content
    return (
      <MainRotationsContent
        selectedTeamId={selectedTeamId}
        setSelectedTeamId={setSelectedTeamId}
        selectedGameId={selectedGameId}
        setSelectedGameId={setSelectedGameId}
        games={games}
        gamesLoading={gamesLoading}
        gamesError={gamesError}
        rotation={rotation}
        rotationLoading={rotationLoading}
        rotationError={rotationError}
      />
    );
  };

  return (
    <Stack
      sx={{
        minHeight: '100vh',
        backgroundColor: ROTATION_COLORS.bg.page,
        color: 'common.white',
        fontFamily: 'Gilroy',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Helmet>
        <title>Stokastic Rotations - NBA Rotation Visualization</title>
        <meta name="description" content="NBA player rotation and stint visualization tool" />
      </Helmet>
      <MobileCover>
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
          <Header />
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
            {renderContent()}
          </Box>
        </Box>
      </MobileCover>
    </Stack>
  );
};

const MainRotationsContent: React.FC<{
  selectedTeamId: string | null;
  setSelectedTeamId: (v: string | null) => void;
  selectedGameId: string | null;
  setSelectedGameId: (v: string | null) => void;
  games: any;
  gamesLoading: boolean;
  gamesError: any;
  rotation: any;
  rotationLoading: boolean;
  rotationError: any;
}> = ({
  selectedTeamId,
  setSelectedTeamId,
  selectedGameId,
  setSelectedGameId,
  games,
  gamesLoading,
  gamesError,
  rotation,
  rotationLoading,
  rotationError,
}) => {
  const handleTeamChange = (event: SelectChangeEvent<string>) => {
    const teamId = event.target.value;
    setSelectedTeamId(teamId || null);
    setSelectedGameId(null);
  };

  const handleGameChange = (event: SelectChangeEvent<string>) => {
    const gameId = event.target.value;
    setSelectedGameId(gameId || null);
  };

  const selectedTeam = NBA_TEAMS.find(t => t.id === selectedTeamId);

  // Helper to format game display text
  const formatGameText = (game: any) => {
    const isHome = game.homeTeamId === selectedTeamId;
    const opponent = isHome ? game.awayTeamAbbrev : game.homeTeamAbbrev;
    const teamScore = isHome ? game.homeScore : game.awayScore;
    const oppScore = isHome ? game.awayScore : game.homeScore;
    const result = teamScore > oppScore ? 'W' : 'L';
    const dateStr = new Date(game.gameDate).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
    return `${result} ${teamScore}-${oppScore} ${isHome ? 'vs' : '@'} ${opponent} — ${dateStr}`;
  };

  const formControlSx = {
    minWidth: 280,
    '& .MuiOutlinedInput-root': {
      color: 'common.white',
      backgroundColor: ROTATION_COLORS.bg.card,
      borderRadius: '6px',
      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.30)', borderWidth: 1 },
      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.50)' },
      '&.Mui-disabled': {
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.15)' },
      },
    },
    '& .MuiInputLabel-root': {
      color: 'common.white',
      '&.Mui-disabled': { color: 'rgba(255, 255, 255, 0.4)' },
    },
    '& .MuiSelect-icon': {
      color: 'common.white',
      '&.Mui-disabled': { color: 'rgba(255, 255, 255, 0.3)' },
    },
    '& .MuiSelect-select': { py: 1.5, px: 2, fontWeight: 500 },
  };
  const menuPaperSx = {
    backgroundColor: ROTATION_COLORS.bg.card,
    color: 'white',
    maxHeight: 400,
    border: '1px solid rgba(255, 255, 255, 0.2)',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
  };
  const menuItemSx = {
    py: 1.5,
    px: 2,
    '&:hover': { backgroundColor: 'rgba(74, 159, 157, 0.2)' },
    '&.Mui-selected': {
      backgroundColor: 'rgba(74, 159, 157, 0.4)',
      '&:hover': { backgroundColor: 'rgba(74, 159, 157, 0.5)' },
    },
  };
  const emptyStateSx = { textAlign: 'center', color: 'rgba(255, 255, 255, 0.6)', p: 4 };

  // Determine game selector label based on state
  const getGameSelectorLabel = () => {
    if (!selectedTeamId) return 'Select Game';
    if (gamesLoading) return 'Loading games...';
    if (gamesError) return 'Error loading games';
    if (games && games.length === 0) return 'No games found';
    return 'Select Game';
  };

  return (
    <Box sx={{ flexGrow: 1, p: 2 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 700, fontFamily: 'Gilroy, sans-serif' }}>
        Stokastic Rotations
      </Typography>

      {/* Team and Game Selectors - Same Row */}
      <Stack direction="row" sx={{ gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        {/* Team Selector */}
        <FormControl variant="outlined" sx={formControlSx}>
          <InputLabel id="team-select-label">Select Team</InputLabel>
          <Select
            labelId="team-select-label"
            value={selectedTeamId || ''}
            onChange={handleTeamChange}
            label="Select Team"
            MenuProps={{
              PaperProps: { sx: menuPaperSx },
            }}
          >
            <MenuItem value="">
              <em>Select a team...</em>
            </MenuItem>
            {NBA_TEAMS.map((team) => (
              <MenuItem key={team.id} value={team.id} sx={menuItemSx}>
                {team.abbrev} - {team.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Game Selector */}
        <FormControl
          variant="outlined"
          sx={formControlSx}
          disabled={!selectedTeamId || gamesLoading || !!gamesError || (games && games.length === 0)}
        >
          <InputLabel id="game-select-label">{getGameSelectorLabel()}</InputLabel>
          <Select
            labelId="game-select-label"
            value={selectedGameId || ''}
            onChange={handleGameChange}
            label={getGameSelectorLabel()}
            MenuProps={{
              PaperProps: { sx: menuPaperSx },
            }}
            endAdornment={
              selectedTeamId && gamesLoading ? (
                <CircularProgress
                  size={20}
                  sx={{
                    color: ROTATION_COLORS.accent.teal,
                    position: 'absolute',
                    right: 36,
                  }}
                />
              ) : null
            }
          >
            <MenuItem value="">
              <em>Select a game...</em>
            </MenuItem>
            {games && games.map((game: any) => (
              <MenuItem key={game.gameId} value={game.gameId} sx={menuItemSx}>
                {formatGameText(game)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {/* Error message for games */}
      {gamesError && selectedTeamId && (
        <Box sx={{ ...emptyStateSx, p: 2, mb: 2 }}>
          <Typography>Failed to load games. Please try again.</Typography>
        </Box>
      )}

      {/* No games message */}
      {games && games.length === 0 && selectedTeamId && !gamesLoading && (
        <Box sx={{ ...emptyStateSx, p: 2, mb: 2 }}>
          <Typography>No recent games found for {selectedTeam?.name}</Typography>
        </Box>
      )}

      {/* Rotation Chart */}
      {selectedGameId && (
        <>
          {rotationLoading && (
            <RotationEmptyState
              message="Loading rotation data..."
              subMessage="Fetching player stints and performance"
              isLoading
            />
          )}

          {rotationError && (
            <Box sx={emptyStateSx}>
              <Typography>Failed to load rotation data. Please try again.</Typography>
            </Box>
          )}

          {rotation && <RotationChart data={rotation} />}
        </>
      )}

      {/* Empty state - No team selected */}
      {!selectedTeamId && (
        <RotationEmptyState
          message="Select a team to get started"
          subMessage="Then choose a game to see player rotations and stint performance"
        />
      )}

      {/* Empty state - Team selected but no game yet */}
      {selectedTeamId && !selectedGameId && !gamesLoading && games && games.length > 0 && (
        <RotationEmptyState
          message="Select a game to view rotations"
          subMessage="See how players were used throughout the game"
        />
      )}
    </Box>
  );
};

const Rotations: React.FC = () => {
  return (
    <ThemeProvider theme={darkTheme}>
      <QueryClientProvider client={queryClient}>
        <Authentication app="ROTATIONS">
          <RotationsContent />
        </Authentication>
      </QueryClientProvider>
    </ThemeProvider>
  );
};

export default Rotations;
